import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';

const emptyFormData = {
  title: '',
  description: '',
  address: '',
  client: '',
  initial_value: '',
  scheduled_date: '',
  scheduled_time: '',
  duration_hours: '',
};

function Jobs() {
  const { canCreateData } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle query params for opening modal with pre-selected client
  useEffect(() => {
    if (!loading && searchParams.get('newJob') === 'true') {
      const clientId = searchParams.get('clientId');
      if (clientId && canCreateData) {
        setFormData({ ...emptyFormData, client: clientId });
        setShowModal(true);
        // Clear the query params
        setSearchParams({});
      }
    }
  }, [loading, searchParams, canCreateData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsData, clientsData] = await Promise.all([
        api.getJobs(),
        api.getClients(),
      ]);
      setJobs(jobsData);
      setClients(clientsData);
    } catch (err) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs based on search and client filter
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = !searchQuery ||
      job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.client_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesClient = !selectedClient || job.client === selectedClient;

    return matchesSearch && matchesClient;
  });

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-800';
  };

  // Format currency
  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  const openAddModal = () => {
    if (!canCreateData) return; // Guard: trial expired
    setFormData(emptyFormData);
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateData) return; // Guard: trial expired
    setFormError('');

    if (!formData.title.trim()) {
      setFormError('Job title is required');
      return;
    }

    if (!formData.client) {
      setFormError('Client is required');
      return;
    }

    setSaving(true);
    try {
      const jobData = {
        title: formData.title,
        description: formData.description,
        address: formData.address,
        initial_value: formData.initial_value || null,
        scheduled_date: formData.scheduled_date || null,
        scheduled_time: formData.scheduled_time || null,
        duration_hours: formData.duration_hours || null,
      };
      if (formData.client) {
        jobData.client_id = formData.client;
      }
      await api.createJob(jobData);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setFormError(err.message || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Jobs</h1>
            <p className="text-slate-600">Manage your jobs and projects</p>
          </div>
          <button
            onClick={openAddModal}
            disabled={!canCreateData}
            className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition shadow-lg ${
              canCreateData
                ? 'bg-secondary text-white hover:bg-orange-600'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            title={!canCreateData ? 'Upgrade to Pro to create new jobs' : ''}
          >
            <i className={`fas ${canCreateData ? 'fa-plus' : 'fa-lock'}`}></i>
            <span>Add Job</span>
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
              />
            </div>
          </div>
          <div className="md:w-64">
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Jobs List */}
        {filteredJobs.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-briefcase text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Jobs Found</h3>
            <p className="text-slate-600 mb-6">
              {jobs.length === 0
                ? 'Create your first job to get started.'
                : 'No jobs match your current filters.'}
            </p>
            {jobs.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedClient('');
                }}
                className="text-secondary font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredJobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition block"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {job.job_number && (
                        <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          #{job.job_number.replace('JOB-', '')}
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-primary">{job.title}</h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(job.status)}`}>
                        {job.status || 'Active'}
                      </span>
                    </div>
                    {job.client_name && (
                      <p className="text-slate-600 mb-1">
                        <i className="fas fa-user mr-2 text-slate-400"></i>
                        {job.client_name}
                      </p>
                    )}
                    {job.address && (
                      <p className="text-slate-500 text-sm">
                        <i className="fas fa-map-marker-alt mr-2 text-slate-400"></i>
                        {job.address}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {job.initial_value && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Initial Value</p>
                        <p className="text-lg font-bold text-primary">
                          {formatCurrency(job.initial_value)}
                        </p>
                      </div>
                    )}
                    {job.scheduled_date && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Scheduled</p>
                        <p className="text-sm font-medium text-slate-700">
                          {new Date(job.scheduled_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredJobs.length > 0 && (
          <div className="mt-6 text-center text-slate-600">
            Showing {filteredJobs.length} of {jobs.length} jobs
          </div>
        )}

        {/* Add Job Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-primary">Add Job</h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-2xl"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {formError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="Kitchen Renovation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Client *
                    </label>
                    <select
                      value={formData.client}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white"
                    >
                      <option value="">Select a client</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Job Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="123 Main St, City, State"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Initial Value ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.initial_value}
                      onChange={(e) => setFormData({ ...formData, initial_value: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="5000.00"
                    />
                  </div>

                  {/* Scheduling Section */}
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-sm font-bold text-slate-700 mb-3">
                      <i className="fas fa-calendar-alt mr-2 text-secondary"></i>
                      Scheduling (Optional)
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Date
                        </label>
                        <input
                          type="date"
                          value={formData.scheduled_date}
                          onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-secondary focus:outline-none transition text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Time
                        </label>
                        <input
                          type="time"
                          value={formData.scheduled_time}
                          onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-secondary focus:outline-none transition text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Duration (hours)
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        max="24"
                        value={formData.duration_hours}
                        onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-secondary focus:outline-none transition text-sm"
                        placeholder="2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition resize-none"
                      placeholder="Job details and scope of work..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 px-6 py-3 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition disabled:opacity-50"
                    >
                      {saving ? 'Creating...' : 'Create Job'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Jobs;
